/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from "axios";
import {
  API_CONFIG,
  DEFAULT_HEADERS,
  HTTP_STATUS,
  ERROR_MESSAGES,
} from "./api";
import { getIsLoggingOut } from "@/auth/useAuthService";
import { checkTokenExpiration, getLocalToken } from "@/auth/LocalStorage";
import { logout } from "@/auth/authUtils";

/**
 * Cliente HTTP global configurado con interceptores
 * Maneja autenticación, errores, encoding de URLs y logging
 */

const encodeRFC3986 = (str: string) =>
  encodeURIComponent(str).replace(
    /[!'()*]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`
  );

// Serializador de params
const serializeRFC3986 = (params: Record<string, unknown>): string =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .flatMap(([k, v]) => {
      if (Array.isArray(v)) {
        return v.map(
          (iv) => `${encodeRFC3986(k)}=${encodeRFC3986(String(iv))}`
        );
      }
      return `${encodeRFC3986(k)}=${encodeRFC3986(String(v))}`;
    })
    .join("&");

class HttpClient {
  private backofficeClient: AxiosInstance;  // Cliente para BACKOFFICE (con auth)
  private genericClient: AxiosInstance;     // Cliente para GENERIC (sin auth)

  constructor() {
    // Cliente para BACKOFFICE (con autenticación)
    this.backofficeClient = axios.create({
      baseURL: API_CONFIG.BACKOFFICE_BASE_URL,
      timeout: API_CONFIG.REQUEST_TIMEOUT,
      headers: DEFAULT_HEADERS,
      paramsSerializer: (params) =>
        serializeRFC3986(params as Record<string, unknown>),
    });

    // Cliente para GENERIC (sin autenticación)
    this.genericClient = axios.create({
      baseURL: API_CONFIG.GENERIC_BASE_URL,
      timeout: API_CONFIG.REQUEST_TIMEOUT,
      headers: DEFAULT_HEADERS,
      paramsSerializer: (params) =>
        serializeRFC3986(params as Record<string, unknown>),
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Configurar interceptors para BACKOFFICE (con autenticación)
    this.setupBackofficeInterceptors();

    // Configurar interceptors para GENERIC (sin autenticación)
    this.setupGenericInterceptors();
  }

  private setupBackofficeInterceptors() {
    // Request interceptor para BACKOFFICE (con auth)
    this.backofficeClient.interceptors.request.use(
      (config) => {
        // Obtener tokens y validar expiración preventivamente
        const tokens = getLocalToken();
        
        if (tokens) {
          // Si el token expiró, cerrar sesión y cancelar la petición
          if (checkTokenExpiration(tokens.accessToken)) {
            logout();
            throw new axios.Cancel("Session expired");
          }
          
          // Inyectar header de autorización
          config.headers.Authorization = JSON.stringify(tokens);
        }

        // Encoding automático de parámetros de query
        if (config.params) {
          config.params = this.encodeQueryParams(config.params);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor para BACKOFFICE
    this.backofficeClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        // No intentar refresh si estamos en proceso de logout
        if (getIsLoggingOut()) {
          return Promise.reject(this.handleError(error));
        }

        const status = error.response?.status;
        
        // Red de seguridad: 401 indica sesión inválida.
        // El 403 NO cierra sesión: el authorizer lo devuelve también cuando el
        // usuario no tiene permiso para un recurso puntual. La expiración del
        // token ya se valida en el request interceptor.
        if (status === HTTP_STATUS.UNAUTHORIZED) {
          logout();
          return Promise.reject(this.handleError(error));
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  private setupGenericInterceptors() {
    // Request interceptor para GENERIC (sin auth)
    this.genericClient.interceptors.request.use(
      (config) => {
        // NO agregar token de autorización para GENERIC

        // Encoding automático de parámetros de query
        if (config.params) {
          config.params = this.encodeQueryParams(config.params);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor para GENERIC
    this.genericClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Determina qué cliente usar según la URL
   */
  private getClientForEndpoint(url: string): AxiosInstance {
    const backofficeBaseUrl = API_CONFIG.BACKOFFICE_BASE_URL;
    if (backofficeBaseUrl && url.startsWith(backofficeBaseUrl)) {
      return this.backofficeClient;
    }

    const genericBaseUrl = API_CONFIG.GENERIC_BASE_URL;

    if (genericBaseUrl && url.startsWith(genericBaseUrl)) {
      return this.genericClient;
    }

    return this.backofficeClient;
  }



  private encodeQueryParams(params: Record<string, any>): Record<string, any> {
    const encodedParams: Record<string, any> = {};

    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined) {
        // Si es string, hacer trim y encoding
        if (typeof value === "string") {
          const trimmedValue = value.trim();
          if (trimmedValue) {
            // Axios ya hace el encoding automáticamente, pero podemos hacer trim
            encodedParams[key] = trimmedValue;
          }
        } else {
          encodedParams[key] = value;
        }
      }
    }

    return encodedParams;
  }

  /**
   * Maneja errores HTTP
   */
  private handleError(error: AxiosError): Error {
    const status = error.response?.status;
    const responseData = error.response?.data as any;

    // Crear error personalizado con información detallada
    let errorMessage: string = ERROR_MESSAGES.NETWORK_ERROR;
    let errorDetails = "";

    switch (status) {
      case HTTP_STATUS.BAD_REQUEST:
        errorMessage = responseData?.message || ERROR_MESSAGES.BAD_REQUEST;
        errorDetails = `Endpoint: ${error.config?.method?.toUpperCase()} ${
          error.config?.url
        }`;
        if (error.config?.params) {
          errorDetails += `\nParámetros: ${JSON.stringify(
            error.config.params,
            null,
            2
          )}`;
        }
        break;

      case HTTP_STATUS.UNAUTHORIZED:
        errorMessage = ERROR_MESSAGES.UNAUTHORIZED;
        break;

      case HTTP_STATUS.FORBIDDEN:
        errorMessage = "No tienes permisos para acceder a este recurso.";
        break;

      case HTTP_STATUS.NOT_FOUND:
        errorMessage = ERROR_MESSAGES.NOT_FOUND;
        break;

      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
        errorMessage = ERROR_MESSAGES.SERVER_ERROR;
        break;
      case HTTP_STATUS.LOCKED:
        errorMessage = "El recurso está bloqueado.";
       
        break; 

      default:
        if (error.code === "ECONNABORTED") {
          errorMessage = ERROR_MESSAGES.TIMEOUT;
        } else if (!error.response) {
          errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
        }
    }

    // Crear error con información adicional para debugging
    const customError = new Error(errorMessage);
    (customError as any).status = status;
    (customError as any).details = errorDetails;
    (customError as any).originalError = error;
    (customError as any).responseData = responseData;

    return customError;
  }

  // Métodos públicos - automáticamente seleccionan el cliente correcto según la URL
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const client = this.getClientForEndpoint(url);
    const response = await client.get(url, config);
    return response.data;
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const client = this.getClientForEndpoint(url);
    const response = await client.post(url, data, config);
    return response.data;
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const client = this.getClientForEndpoint(url);
    const response = await client.put(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const client = this.getClientForEndpoint(url);
    const response = await client.delete(url, config);
    return response.data;
  }

  // Métodos explícitos para casos específicos
  async getBackoffice<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.backofficeClient.get(url, config);
    return response.data;
  }

  async getGeneric<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.genericClient.get(url, config);
    return response.data;
  }

  // Método para obtener las instancias de axios si se necesita funcionalidad avanzada
  getBackofficeAxiosInstance(): AxiosInstance {
    return this.backofficeClient;
  }

  getGenericAxiosInstance(): AxiosInstance {
    return this.genericClient;
  }
}

// Exportar instancia singleton
export const httpClient = new HttpClient();
export default httpClient;
